"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { ContentCard, type ImageComponent } from "fansunited-sports-ui";
import { FillImage } from "@/components/ui/FillImage";
import { MediaVariantsProvider } from "@/components/ui/MediaVariantsProvider";
import { getPostImageUrl } from "@/components/ui/PostImage";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import type { Post } from "@/payload-types";
import type { NewsBlogCategorySectionData } from "../types";
export interface NewsBlogCategorySectionProps {
  data: NewsBlogCategorySectionData;
  /** Lead image is the page's LCP candidate: eager load + `fetchpriority="high"`. Only the first hero on a page. */
  priority?: boolean;
}

/**
 * NewsBlogCategorySection - Articles-only homepage section for a category.
 *
 * Layout: "Lead + Secondary + Mini Stack"
 *   - Header: category title (link) + optional description + "View all" CTA
 *   - Lead (2/3 width): big 16:9 hero
 *   - Right column (1/3 width):
 *       - Secondary hero (16:9, smaller — matches column width)
 *       - Mini-headline list beneath, growing to fill remaining height
 *
 * Default: 5 articles (1 lead + 1 secondary + 3 mini). Max: 6.
 */
export function NewsBlogCategorySection(t0) {
  const $ = _c(39);
  const {
    data,
    priority: t1
  } = t0;
  const priority = t1 === undefined ? false : t1;
  const labels = useLabels();
  const nav = useNavigation();
  const {
    slug,
    title,
    description,
    articles
  } = data;
  if (articles.length === 0) {
    return null;
  }
  const lead = articles[0];
  const secondary = articles[1];
  let t2;
  if ($[0] !== articles) {
    t2 = articles.slice(2);
    $[0] = articles;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const minis = t2;
  let t3;
  if ($[2] !== nav || $[3] !== slug) {
    t3 = nav.category(slug);
    $[2] = nav;
    $[3] = slug;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const categoryHref = t3;
  let t4;
  if ($[5] !== title) {
    t4 = <span className="truncate">{title}</span>;
    $[5] = title;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChevronRight className="w-4 h-4 shrink-0" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== categoryHref || $[9] !== t4) {
    t6 = <Link href={categoryHref} prefetch={false} className="flex items-center gap-1 text-lg font-bold link-themed hover:underline">{t4}{t5}</Link>;
    $[8] = categoryHref;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== description) {
    t7 = description && <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{description}</p>;
    $[11] = description;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <div className="min-w-0">{t6}{t7}</div>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== categoryHref || $[17] !== labels.homePage.viewAll) {
    t9 = <Link href={categoryHref} prefetch={false} className="text-sm font-medium text-accent hover:text-accent/80 shrink-0 mt-1">{labels.homePage.viewAll}</Link>;
    $[16] = categoryHref;
    $[17] = labels.homePage.viewAll;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <div className="flex items-start justify-between gap-4">{t8}{t9}</div>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== articles) {
    t11 = articles.map(_temp);
    $[22] = articles;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== lead || $[25] !== priority) {
    t12 = <div className="lg:col-span-2"><LeadCard article={lead} priority={priority} /></div>;
    $[24] = lead;
    $[25] = priority;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== minis || $[28] !== secondary) {
    t13 = (secondary || minis.length > 0) && <div className="lg:col-span-1 flex flex-col gap-4">{secondary && <SecondaryHero article={secondary} />}{minis.length > 0 && <MiniStack articles={minis} />}</div>;
    $[27] = minis;
    $[28] = secondary;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t12 || $[31] !== t13) {
    t14 = <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">{t12}{t13}</div>;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t11 || $[34] !== t14) {
    t15 = <MediaVariantsProvider media={t11}>{t14}</MediaVariantsProvider>;
    $[33] = t11;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t10 || $[37] !== t15) {
    t16 = <section className="space-y-4">{t10}{t15}</section>;
    $[36] = t10;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  return t16;
}
function _temp(post) {
  return post.heroImage;
}
const HERO_GRADIENT = "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.5) 30%, transparent 50%)";
function LeadCard(t0) {
  const $ = _c(21);
  const {
    article,
    priority
  } = t0;
  const nav = useNavigation();
  const t1 = article.heroImage;
  const t2 = String(article.id);
  let t3;
  if ($[0] !== article || $[1] !== nav) {
    t3 = nav.post(article);
    $[0] = article;
    $[1] = nav;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = article.title;
  const t5 = article.summary || undefined;
  let t6;
  if ($[3] !== article) {
    t6 = getPostImageUrl(article, "xlarge");
    $[3] = article;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== article.title || $[6] !== t6) {
    t7 = {
      url: t6,
      alt: article.title
    };
    $[5] = article.title;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== article.title || $[9] !== t2 || $[10] !== t3 || $[11] !== t5 || $[12] !== t7) {
    t8 = {
      id: t2,
      type: "article",
      link: t3,
      title: t4,
      description: t5,
      image: t7,
      gradient: HERO_GRADIENT
    };
    $[8] = article.title;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      shadow: "none",
      imageAspectRatio: "16/9",
      typography: {
        title: "text-xl md:text-2xl lg:text-3xl font-bold mb-1!",
        description: "text-sm text-white/80 line-clamp-2 hidden md:block"
      }
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8) {
    t10 = <ContentCard content={t8} variant="hero" ImageComponent={FillImage as ImageComponent} styles={t9} />;
    $[15] = t8;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== article.heroImage || $[18] !== priority || $[19] !== t10) {
    t11 = <MediaVariantsProvider media={t1} sizes="(max-width: 1024px) 100vw, 66vw" priority={priority}>{t10}</MediaVariantsProvider>;
    $[17] = article.heroImage;
    $[18] = priority;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
function SecondaryHero(t0) {
  const $ = _c(16);
  const {
    article
  } = t0;
  const nav = useNavigation();
  const t1 = String(article.id);
  let t2;
  if ($[0] !== article || $[1] !== nav) {
    t2 = nav.post(article);
    $[0] = article;
    $[1] = nav;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = article.title;
  let t4;
  if ($[3] !== article) {
    t4 = getPostImageUrl(article, "large");
    $[3] = article;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== article.title || $[6] !== t4) {
    t5 = {
      url: t4,
      alt: article.title
    };
    $[5] = article.title;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== article.title || $[9] !== t1 || $[10] !== t2 || $[11] !== t5) {
    t6 = {
      id: t1,
      type: "article",
      link: t2,
      title: t3,
      image: t5,
      gradient: HERO_GRADIENT
    };
    $[8] = article.title;
    $[9] = t1;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      shadow: "none",
      imageAspectRatio: "16/9",
      typography: {
        title: "text-base md:text-lg font-bold mb-0! leading-snug"
      }
    };
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6) {
    t8 = <ContentCard content={t6} variant="hero" ImageComponent={FillImage as ImageComponent} styles={t7} />;
    $[14] = t6;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}

/**
 * MiniStack - Vertical list of compact article cards filling its container.
 * Each card has a small 16:9 thumbnail on the left and a title on the right.
 * Uses flex-1 per item so the stack fills the remaining column height.
 */
function MiniStack(t0) {
  const $ = _c(7);
  const {
    articles
  } = t0;
  const nav = useNavigation();
  let t1;
  if ($[0] !== articles || $[1] !== nav) {
    let t2;
    if ($[3] !== nav) {
      t2 = post => {
        const imageUrl = getPostImageUrl(post, "medium");
        return <Link key={post.id} href={nav.post(post)} prefetch={false} className="group flex gap-3 items-center flex-1 min-h-0">{imageUrl && <div className="w-24 aspect-video rounded-md overflow-hidden shrink-0 bg-muted"><img src={imageUrl} alt={post.title} className="w-full h-full object-cover" /></div>}<h3 className="text-sm font-semibold leading-snug line-clamp-2 group-hover:underline">{post.title}</h3></Link>;
      };
      $[3] = nav;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = articles.map(t2);
    $[0] = articles;
    $[1] = nav;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="flex flex-col gap-3 flex-1 min-h-0">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}